import {DialogFragment, PageFragment, PageFragmentProps} from "@/shared/PageFragment";
import {Box} from "@mui/material";
import {FormGenContainer, FormGenContainerMode} from "@/shared/FormGenContainer";
import {DW_LG} from "@/shared/dimens";
import {BaseApp, DIALOG_FLAG_DISABLE_BACKDROP_CLICK, DIALOG_FLAG_SHOW_CLOSE} from "@/shared/BaseApp";
import React from "react";
import {LabelerTeamMember, ResourceData} from "@/fyneai-shared/types";
import {JsonObject} from "@/shared/json/json-object";
import {FormGen} from "@/shared/formgen";
import {JsonProperty} from "@/shared/json/json-property";
import {sleep} from "@/shared/sleep";
import {Api} from "@/fyneai-shared/api";

export class EditTeamMemberHelper {

  static addTeamMember() {
    BaseApp.CONTEXT.showDialog(
      {flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_DISABLE_BACKDROP_CLICK},
      () => <EditLabelerTeamMemberFragment
        invite={new LabelerTeamMemberInvite()}
        onLabelerTeamMemberSaved={teamMember => BaseApp.CONTEXT.hideDialog()}/>);
  }

  static editTeamMember(teamMember: LabelerTeamMember) {
    BaseApp.CONTEXT.showDialog(
      {flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_DISABLE_BACKDROP_CLICK},
      () => <EditLabelerTeamMemberFragment edit={teamMember}
                                           onLabelerTeamMemberSaved={teamMember => BaseApp.CONTEXT.hideDialog()}/>);
  }

  static deleteTeamMember(teamMember: LabelerTeamMember) {
    ResourceData.project().labeler.teamMembers.deleteListItem(teamMember)
      .then(() => BaseApp.CONTEXT.showToast("Deleted."));
  }
}

type EditLabelerTeamMemberFragmentProps = PageFragmentProps & {
  invite?: LabelerTeamMemberInvite,
  edit?: LabelerTeamMember,
  onLabelerTeamMemberSaved: (teamMember: LabelerTeamMember) => void,
}

@JsonObject()
export class LabelerTeamMemberInvite {

  @FormGen({
    name: "Email",
    type: "email",
    required: true,
    noneditable: true,
    description: "An account will be autogenerated for your invitee with a temporary password and a login link sent to this email address.",
  })
  @JsonProperty()
  readonly email: string;

  @FormGen({
    name: "Firstname",
    type: "string",
    required: true,
  })
  @JsonProperty()
  readonly firstname: string;

  @FormGen({
    name: "Lastname",
    type: "string",
    required: true,
  })
  @JsonProperty()
  readonly lastname: string;

  constructor() {
  }
}

class EditLabelerTeamMemberFragment extends DialogFragment<EditLabelerTeamMemberFragmentProps> {

  private readonly labelerTeamMembers = ResourceData.project().labeler.teamMembers;

  protected onCreateState() {
    return {
      ...super.onCreateState(),
      title: this.props.edit ? "Edit" : "Add",
    };
  }

  protected styleFlags(): number {
    return PageFragment.STYLE_ACTION_PANEL_TYPE_BUTTON_FLAG;
  }

  protected getActionButtonText(): string {
    return this.props.edit ? "Save" : "Add";
  }

  onActionButtonClicked() {
    const onLabelerTeamMemberSaved = this.props.onLabelerTeamMemberSaved;
    const invite = this.props.invite;
    if (Boolean(invite)) {
      BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
        await sleep(1000);
        const teamMember = await Api.getInstance().labelerTeamMemberInvite(ResourceData.projectId(), invite.email, invite.firstname, invite.lastname);
        if (teamMember) {
          BaseApp.CONTEXT.showToast("Team member added.");
          onLabelerTeamMemberSaved(teamMember);
        } else {
          BaseApp.CONTEXT.showToast("Oops, something went wrong.");
        }
      });
    } else if (Boolean(this.props.edit)) {
      this.labelerTeamMembers.addListItem(this.props.edit)
        .then(() => onLabelerTeamMemberSaved(this.props.edit));
    }
  }

  renderContent(): React.ReactElement | null {
    return <Box style={{display: "flex", width: DW_LG}}>
      <FormGenContainer
        mode={Boolean(this.props.edit) ? FormGenContainerMode.EDIT : FormGenContainerMode.CREATE}
        content={Boolean(this.props.edit) ? this.props.edit : this.props.invite}
        saveActionButtonProvider={this}/>
    </Box>;
  }
}
